import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import CreateGenericCodebookItemInteractor from "../interactor/CreateGenericCodebookItemInteractor";
import GetGenericCodebookInteractor from "../interactor/GetGenericCodebookInteractor";
import UpdateGenericCodebookItemInteractor from "../interactor/UpdateGenericCodebookItemInteractor";
import { ICodebook } from "../model/Codebook";
import { IGenericCodebookItem } from "../model/GenericCodebookItem";

export interface TGenericCodebookPresenter extends TLoadingAwarePresenter {
  codebookTypeList: ICodebook[];
  selectedCodebook?: string;
  codebookItemList?: IGenericCodebookItem[];
  itemModalVisible: boolean;
}

export interface IGenericCodebookPresenter
  extends TGenericCodebookPresenter,
    TPresentable {
  loadCodebookItemsList(codebookItemList: IGenericCodebookItem[]): void;
  updateGenericCodebookItem(data: IGenericCodebookItem): void;
  createGenericCodebookItem(data: IGenericCodebookItem): void;
  onChangeItemModalVisible(itemModalVisible: boolean): void;
  createCodebook(data: IGenericCodebookItem): void;
}

const defaultState: TGenericCodebookPresenter = {
  codebookTypeList: [],
  selectedCodebook: undefined,
  codebookItemList: [],
  itemModalVisible: false
};

const GenericCodebookPresenter = withStore<
  IGenericCodebookPresenter,
  TGenericCodebookPresenter
>(({ application, store, loader, translate }): IGenericCodebookPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TGenericCodebookPresenter>();

  const loadCodebookItemsList = (codebookItemList: IGenericCodebookItem[]) => {
    loader.start("codebookItemsLoader");
    _store.update({
      codebookItemList
    });
    loader.stop("codebookItemsLoader");
  };

  const getCodebookItemsByType = async (codebookType: string) => {
    const codebookItemList =
      codebookType &&
      (await application.container
        .resolve<GetGenericCodebookInteractor>("getGenericCodebook")
        .execute(codebookType, true));
    _store.update({
      codebookItemList
    });
    loader.stop("codebookItemsLoader");
  };

  const updateGenericCodebookItem = async (data: IGenericCodebookItem) => {
    const codebookType = _store.getState<TGenericCodebookPresenter>()
      .selectedCodebook;
    if (codebookType) {
      let codebookItem = data;
      codebookItem.type = codebookType;
      await _application.container
        .resolve<UpdateGenericCodebookItemInteractor>(
          "updateGenericCodebookItem"
        )
        .execute(data);
      loader.start("codebookItemsLoader");
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("generic_codebook.messages.codebook_item_update_success")
        );
      await getCodebookItemsByType(codebookType);
    }
  };

  const createGenericCodebookItem = async (data: IGenericCodebookItem) => {
    const codebookType = _store.getState<TGenericCodebookPresenter>()
      .selectedCodebook;
    if (codebookType) {
      let codebookItem = data;
      codebookItem.type = codebookType;
      await _application.container
        .resolve<CreateGenericCodebookItemInteractor>(
          "createGenericCodebookItem"
        )
        .execute(data);
      loader.start("codebookItemsLoader");
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("generic_codebook.messages.codebook_item_create_success")
        );
      await getCodebookItemsByType(codebookType);
    }
  };

  const onChangeItemModalVisible = (itemModalVisible: boolean) => {
    _store.update({
      itemModalVisible
    });
  };

  const createCodebook = async (data: IGenericCodebookItem) => {
    await _application.container
      .resolve<CreateGenericCodebookItemInteractor>("createGenericCodebookItem")
      .execute(data);

    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(
        translate("generic_codebook.messages.codebook_type_create_success")
      );
    _store.update({
      selectedCodebook: data.type
    });
    _application.navigator.push({
      pathname: `/codebooks/generic/${data.type}`
    });
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadCodebookItemsList,
    updateGenericCodebookItem,
    createGenericCodebookItem,
    onChangeItemModalVisible,
    createCodebook
  };
}, defaultState);

export default GenericCodebookPresenter;
